import { useState, useMemo } from 'react'
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Table,
  Input,
  HStack,
  VStack,
  Card,
  IconButton,
  Progress,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  Switch,
} from '@chakra-ui/react'
import { Link2, Search, ArrowLeft, Check, Users } from 'lucide-react'
import { useAccountsLettrageStats } from '../hooks'
import { useAccountingContext } from '../context/AccountingContext'
import { LettrageWorkspace } from './LettrageWorkspace'
import type { AccountLettrageStats } from '../types'

interface LettrageModalProps {
  isOpen: boolean
  onClose: () => void
}

export function LettrageModal({ isOpen, onClose }: LettrageModalProps) {
  const { residenceId, activeFiscalYear } = useAccountingContext()
  const [search, setSearch] = useState('')
  const [showUnletteredOnly, setShowUnletteredOnly] = useState(true)
  const [selectedAccount, setSelectedAccount] = useState<AccountLettrageStats | null>(null)

  const { data: accountsStats, isLoading } = useAccountsLettrageStats({
    residence_id: residenceId,
    fiscal_year_id: activeFiscalYear?.id,
  })

  // Filtrer les comptes
  const filteredAccounts = useMemo(() => {
    if (!accountsStats) return []

    return accountsStats.filter((account) => {
      // Filtre recherche
      const searchLower = search.toLowerCase()
      const matchSearch =
        !search ||
        account.account_code.toLowerCase().includes(searchLower) ||
        account.account_name.toLowerCase().includes(searchLower) ||
        account.owner_name?.toLowerCase().includes(searchLower) ||
        account.unit_number?.toLowerCase().includes(searchLower)

      // Filtre non lettrés uniquement
      const matchUnlettered = !showUnletteredOnly || account.unlettered_line_count > 0

      return matchSearch && matchUnlettered
    })
  }, [accountsStats, search, showUnletteredOnly])

  // Calculer les statistiques globales
  const globalStats = useMemo(() => {
    if (!accountsStats) return { totalAccounts: 0, avgLettered: 0 }

    const totalAccounts = accountsStats.length
    const avgLettered =
      totalAccounts > 0
        ? Math.round(accountsStats.reduce((sum, a) => sum + a.lettered_percentage, 0) / totalAccounts)
        : 0

    return { totalAccounts, avgLettered }
  }, [accountsStats])

  const handleSelectAccount = (account: AccountLettrageStats) => {
    setSelectedAccount(account)
  }

  const handleBackToList = () => {
    setSelectedAccount(null)
  }

  const handleClose = () => {
    setSelectedAccount(null)
    setSearch('')
    onClose()
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="full">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="1200px" maxH="90vh" overflow="hidden">
          <DialogCloseTrigger />
          <DialogHeader borderBottomWidth="1px">
            <HStack gap={3}>
              {selectedAccount && (
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Retour"
                  onClick={handleBackToList}
                >
                  <ArrowLeft size={18} />
                </IconButton>
              )}
              <Link2 size={20} />
              <DialogTitle>
                {selectedAccount
                  ? `Lettrage : ${selectedAccount.owner_name || selectedAccount.account_code}${selectedAccount.unit_number ? ` (Lot ${selectedAccount.unit_number})` : ''}`
                  : 'Lettrage des comptes'}
              </DialogTitle>
            </HStack>
          </DialogHeader>

          <DialogBody p={0} overflow="auto">
            {selectedAccount ? (
              <LettrageWorkspace
                accountId={selectedAccount.account_id}
                unitId={selectedAccount.unit_id}
                accountCode={selectedAccount.account_code}
                ownerName={selectedAccount.owner_name}
                unitNumber={selectedAccount.unit_number}
              />
            ) : (
              <Box p={6}>
                {/* Statistiques globales */}
                <Flex gap={4} mb={6}>
                  <Card.Root flex={1} variant="outline">
                    <Card.Body p={4}>
                      <VStack align="start" gap={1}>
                        <Text fontSize="sm" color="gray.500">
                          Comptes à traiter
                        </Text>
                        <HStack>
                          <Users size={20} color="var(--chakra-colors-brand-500)" />
                          <Text fontSize="2xl" fontWeight="bold">
                            {globalStats.totalAccounts}
                          </Text>
                        </HStack>
                      </VStack>
                    </Card.Body>
                  </Card.Root>

                  <Card.Root flex={1} variant="outline">
                    <Card.Body p={4}>
                      <VStack align="start" gap={1}>
                        <Text fontSize="sm" color="gray.500">
                          Progression lettrage
                        </Text>
                        <HStack w="full">
                          <Check size={20} color="var(--chakra-colors-green-500)" />
                          <Text fontSize="2xl" fontWeight="bold" color="green.600">
                            {globalStats.avgLettered}%
                          </Text>
                        </HStack>
                        <Progress.Root value={globalStats.avgLettered} size="sm" colorPalette="green" w="full">
                          <Progress.Track>
                            <Progress.Range />
                          </Progress.Track>
                        </Progress.Root>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Flex>

                {/* Filtres */}
                <Flex gap={4} mb={4} align="center">
                  <Box position="relative" flex={1} maxW="300px">
                    <Box
                      position="absolute"
                      left={3}
                      top="50%"
                      transform="translateY(-50%)"
                      pointerEvents="none"
                      color="gray.400"
                    >
                      <Search size={16} />
                    </Box>
                    <Input
                      pl={10}
                      placeholder="Rechercher un compte, propriétaire, lot..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Box>

                  <HStack
                    gap={2}
                    cursor="pointer"
                    onClick={() => setShowUnletteredOnly(!showUnletteredOnly)}
                  >
                    <Switch.Root checked={showUnletteredOnly} colorPalette="brand" size="sm">
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Root>
                    <Text fontSize="sm">Non lettrés uniquement</Text>
                  </HStack>
                </Flex>

                {/* Liste des comptes */}
                <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row bg="gray.50">
                        <Table.ColumnHeader>Compte</Table.ColumnHeader>
                        <Table.ColumnHeader>Copropriétaire</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="center">Progression</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {isLoading ? (
                        <Table.Row>
                          <Table.Cell colSpan={4} textAlign="center" py={8}>
                            <Text color="gray.500">Chargement...</Text>
                          </Table.Cell>
                        </Table.Row>
                      ) : filteredAccounts.length === 0 ? (
                        <Table.Row>
                          <Table.Cell colSpan={4} textAlign="center" py={8}>
                            <VStack gap={2}>
                              <Check size={32} color="var(--chakra-colors-green-500)" />
                              <Text color="gray.500">
                                {showUnletteredOnly
                                  ? 'Tous les comptes sont lettrés'
                                  : 'Aucun compte trouvé'}
                              </Text>
                            </VStack>
                          </Table.Cell>
                        </Table.Row>
                      ) : (
                        filteredAccounts.map((account) => (
                          <Table.Row
                            key={`${account.account_id}-${account.unit_id}`}
                            _hover={{ bg: 'gray.50' }}
                          >
                            <Table.Cell>
                              <Text fontFamily="mono" fontWeight="medium" color="blue.700">
                                {account.account_code}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Text>{account.owner_name || '-'}</Text>
                            </Table.Cell>
                            <Table.Cell>
                              <HStack gap={2} justify="center">
                                <Progress.Root
                                  value={account.lettered_percentage}
                                  size="sm"
                                  colorPalette={
                                    account.lettered_percentage === 100
                                      ? 'green'
                                      : account.lettered_percentage >= 50
                                      ? 'orange'
                                      : 'red'
                                  }
                                  w="80px"
                                >
                                  <Progress.Track>
                                    <Progress.Range />
                                  </Progress.Track>
                                </Progress.Root>
                                <Text fontSize="xs" color="gray.500" w="35px">
                                  {account.lettered_percentage}%
                                </Text>
                              </HStack>
                            </Table.Cell>
                            <Table.Cell textAlign="right">
                              {account.unlettered_line_count > 0 ? (
                                <Button
                                  size="xs"
                                  variant="outline"
                                  colorPalette="brand"
                                  onClick={() => handleSelectAccount(account)}
                                >
                                  <Link2 size={14} />
                                  Lettrer
                                </Button>
                              ) : (
                                <Badge colorPalette="green" variant="subtle">
                                  <Check size={12} />
                                  Complet
                                </Badge>
                              )}
                            </Table.Cell>
                          </Table.Row>
                        ))
                      )}
                    </Table.Body>
                  </Table.Root>
                </Box>
              </Box>
            )}
          </DialogBody>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
